import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Check,
  ChevronLeft,
  Plus,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  Trash2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BRAND_SIZE_BUCKETS,
  CreateCredentialRequest,
  OPENROUTER_MODEL_SUGGESTIONS,
  type BrandSizeBucket,
  type Provider,
} from '@sourcito/schemas';
import { useTenantPreferences, useUpdateTenantPreferences } from '../lib/preferences';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../design';
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useValidateCredential,
} from '../lib/credentials';
import { ApiCallError } from '../lib/api';

type ValidationStatus = 'valid' | 'invalid' | 'untested';

const PROVIDERS: Array<{ value: Provider; label: string; helper?: string }> = [
  { value: 'keepa', label: 'Keepa', helper: 'Used for product search and ROI scoring.' },
  {
    value: 'gemini',
    label: 'Google Gemini',
    helper:
      'One key covers supplier-discovery web search (Google-grounded) AND email drafting. Preferred over the OpenRouter + Tavily pair. Get a key at aistudio.google.com.',
  },
  {
    value: 'openrouter',
    label: 'OpenRouter',
    helper:
      'Used for natural-language query translation. Pick any tool-calling-capable model (Claude, Gemini, GPT, Llama, …).',
  },
  {
    value: 'tavily',
    label: 'Tavily (web search)',
    helper: 'Used for supplier discovery. Validated at save time via Tavily /usage (zero credits).',
  },
  { value: 'brave', label: 'Brave (web search)', helper: 'Alternative to Tavily.' },
  { value: 'hunter', label: 'Hunter (contact enrichment)', helper: 'Phase 1.5+ — outreach.' },
  { value: 'sendgrid', label: 'SendGrid (email)', helper: 'Phase 1.5+ — outreach delivery.' },
  { value: 'smtp', label: 'Generic SMTP', helper: 'Phase 1.5+ — outreach delivery (BYO mail).' },
  { value: 'sp_api', label: 'Amazon SP-API', helper: 'v1.5+ — your own developer registration.' },
];

const PROVIDER_LABEL: Record<string, string> = Object.fromEntries(
  PROVIDERS.map((p) => [p.value, p.label])
);

export function CredentialsPage() {
  const { data: credentials, isLoading, isError, error } = useCredentials();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Credentials</h1>
              <p className="text-xs text-fg-tertiary">
                Bring Your Own Keys — your keys, your data, your control.
              </p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
              Add credential
            </Button>
            <DialogContent>
              <AddCredentialForm onSuccess={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10 space-y-6">
        <Card elevated>
          <CardHeader>
            <CardTitle>How credentials work</CardTitle>
            <CardDescription>
              Sourcito stores your API keys encrypted with AES-GCM. Plaintext only crosses the wire
              when you add or use a key — it's never returned to the browser.
            </CardDescription>
          </CardHeader>
          <ul className="text-sm text-fg-secondary space-y-1.5">
            <li>
              • OpenRouter keys are validated at save time — Sourcito confirms the key authenticates
              and the chosen model supports tool calling. Bad picks fail loudly here, not later.
            </li>
            <li>
              • Tavily keys are validated at save time via the zero-credit /usage probe — bad keys
              are rejected before any supplier-discovery search runs.
            </li>
            <li>• Keepa keys can be validated on-demand with the Validate button.</li>
            <li>
              • Other providers store fine but show{' '}
              <code className="font-mono text-xs">untested</code> until those validators ship.
            </li>
            <li>
              • Rotating a key replaces the old one; the audit log records the rotation without the
              keys themselves.
            </li>
          </ul>
        </Card>

        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load credentials: {(error as Error).message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {credentials && credentials.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary mb-4">
              No credentials yet. Add your first one to start using Sourcito's external
              integrations.
            </p>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
              Add credential
            </Button>
          </Card>
        )}

        {credentials && credentials.length > 0 && (
          <div className="space-y-2">
            {credentials.map((cred) => (
              <CredentialRow key={cred.id} cred={cred} />
            ))}
          </div>
        )}

        <BrandSizePreferencesCard />
      </main>
    </div>
  );
}

function CredentialRow({
  cred,
}: {
  cred: {
    id: string;
    provider: string;
    label: string | null;
    validationStatus: ValidationStatus;
    lastValidatedAt: string | null;
    metadata: Record<string, unknown> | null;
  };
}) {
  const validate = useValidateCredential();
  const remove = useDeleteCredential();
  const [feedback, setFeedback] = useState<string | null>(null);

  const model =
    cred.provider === 'openrouter' && typeof cred.metadata?.model === 'string'
      ? cred.metadata.model
      : null;

  return (
    <Card className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-medium">
            {PROVIDER_LABEL[cred.provider] ?? cred.provider}
          </h3>
          {model && (
            <Badge variant="neutral" className="font-mono text-xs">
              {model}
            </Badge>
          )}
          <StatusBadge status={cred.validationStatus} />
        </div>
        <p className="text-sm text-fg-secondary mt-0.5">
          {cred.label || <span className="text-fg-tertiary italic">no label</span>}
          {cred.lastValidatedAt && (
            <>
              {' · last validated '}
              <time dateTime={cred.lastValidatedAt}>
                {new Date(cred.lastValidatedAt).toLocaleString()}
              </time>
            </>
          )}
        </p>
        {feedback && <p className="text-xs text-fg-tertiary mt-1">{feedback}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="secondary"
          size="sm"
          loading={validate.isPending}
          onClick={async () => {
            setFeedback(null);
            try {
              const result = await validate.mutateAsync(cred.id);
              setFeedback(result.ok ? 'Key validated.' : `Failed: ${result.error}`);
            } catch (err) {
              setFeedback(`Validate failed: ${(err as Error).message}`);
            }
          }}
        >
          Validate
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Delete ${cred.provider} credential`}
          loading={remove.isPending}
          onClick={async () => {
            if (!confirm(`Remove the ${PROVIDER_LABEL[cred.provider]} credential?`)) return;
            try {
              await remove.mutateAsync(cred.id);
            } catch (err) {
              setFeedback(`Delete failed: ${(err as Error).message}`);
            }
          }}
        >
          <Trash2 className="size-4 text-fg-secondary" />
        </Button>
      </div>
    </Card>
  );
}

function StatusBadge({ status }: { status: ValidationStatus }) {
  if (status === 'valid') {
    return (
      <Badge variant="positive" className="gap-1">
        <ShieldCheck className="size-3" /> Valid
      </Badge>
    );
  }
  if (status === 'invalid') {
    return (
      <Badge variant="negative" className="gap-1">
        <ShieldAlert className="size-3" /> Invalid
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" className="gap-1">
      <ShieldQuestion className="size-3" /> Untested
    </Badge>
  );
}

function AddCredentialForm({ onSuccess }: { onSuccess: () => void }) {
  const create = useCreateCredential();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<{ provider: Provider; value: string; label?: string; openrouterModel?: string }>({
    resolver: zodResolver(CreateCredentialRequest),
    defaultValues: { provider: 'keepa', value: '', label: '', openrouterModel: '' },
  });

  const provider = watch('provider');
  const providerInfo = PROVIDERS.find((p) => p.value === provider);

  const errorMessage = create.isError
    ? create.error instanceof ApiCallError
      ? create.error.message
      : ((create.error as Error)?.message ?? 'Failed to add credential.')
    : null;

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        try {
          await create.mutateAsync({
            provider: values.provider,
            value: values.value,
            label: values.label?.trim() || undefined,
            openrouterModel:
              values.provider === 'openrouter' ? values.openrouterModel?.trim() : undefined,
          });
          onSuccess();
        } catch {
          // surfaced via create.isError
        }
      })}
      className="space-y-5"
    >
      <DialogHeader>
        <DialogTitle>Add credential</DialogTitle>
        <DialogDescription>
          Stored encrypted at rest with AES-GCM. The plaintext is never returned to the browser
          after this.
        </DialogDescription>
      </DialogHeader>

      <FormField label="Provider" htmlFor="provider" required helper={providerInfo?.helper}>
        <Select value={provider} onValueChange={(v) => setValue('provider', v as Provider)}>
          <SelectTrigger id="provider">
            <SelectValue placeholder="Select a provider" />
          </SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField label="API key" htmlFor="value" error={errors.value?.message} required>
        <Input
          id="value"
          type="password"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={errors.value ? true : undefined}
          {...register('value')}
        />
      </FormField>

      {provider === 'openrouter' && (
        <FormField
          label="Model"
          htmlFor="openrouterModel"
          error={errors.openrouterModel?.message}
          required
          helper="Any tool-calling-capable OpenRouter model. We test it at save time."
        >
          <Input
            id="openrouterModel"
            type="text"
            list="openrouter-model-suggestions"
            autoComplete="off"
            spellCheck={false}
            placeholder="anthropic/claude-haiku-4.5"
            aria-invalid={errors.openrouterModel ? true : undefined}
            {...register('openrouterModel')}
          />
          <datalist id="openrouter-model-suggestions">
            {OPENROUTER_MODEL_SUGGESTIONS.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </FormField>
      )}

      <FormField
        label="Label (optional)"
        htmlFor="label"
        helper="A short note to help you remember this key."
      >
        <Input id="label" type="text" placeholder="My main Keepa account" {...register('label')} />
      </FormField>

      {errorMessage && (
        <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
          <p className="text-sm text-negative">{errorMessage}</p>
        </div>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onSuccess()}
          disabled={isSubmitting || create.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={isSubmitting || create.isPending}
          leadingIcon={<Check className="size-4" />}
        >
          Add credential
        </Button>
      </DialogFooter>
    </form>
  );
}

function BrandSizePreferencesCard() {
  const { data, isLoading } = useTenantPreferences();
  const update = useUpdateTenantPreferences();

  const cap = data?.defaultBrandSizeCap ?? 'small';
  const enabled = data?.brandStatsEnabled ?? true;

  return (
    <Card elevated>
      <CardHeader>
        <CardTitle>Brand-size filter</CardTitle>
        <CardDescription>
          Sourcito caches a brand-size aggregate per Amazon brand (computed from product count and
          sampled review volume). Search results are post-filtered so brands above your cap drop
          out. NL queries like "include big brands" override per-search.
        </CardDescription>
      </CardHeader>

      <div className="space-y-4 mt-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <label htmlFor="brandStatsEnabled" className="text-sm font-medium">
              Brand-stats enabled
            </label>
            <p className="text-xs text-fg-tertiary mt-0.5">
              When off, the size cap is skipped entirely and no background probes fire — saves Keepa
              tokens on cost-sensitive runs.
            </p>
          </div>
          <input
            id="brandStatsEnabled"
            type="checkbox"
            checked={enabled}
            disabled={isLoading || update.isPending}
            onChange={(e) => update.mutate({ brandStatsEnabled: e.target.checked })}
            className="size-5 cursor-pointer"
          />
        </div>

        <div>
          <label htmlFor="defaultBrandSizeCap" className="text-sm font-medium">
            Default size cap
          </label>
          <p className="text-xs text-fg-tertiary mt-0.5 mb-2">
            Buckets up to and including this cap pass the filter. Choose <em>micro</em> for the
            tightest small-brand bias; <em>large</em> disables the cap.
          </p>
          <div className="flex flex-wrap gap-2">
            {BRAND_SIZE_BUCKETS.map((bucket) => (
              <button
                key={bucket}
                type="button"
                disabled={!enabled || isLoading || update.isPending}
                onClick={() => update.mutate({ defaultBrandSizeCap: bucket as BrandSizeBucket })}
                className={`px-3 py-1.5 rounded-xs text-xs font-medium capitalize border transition-colors duration-fast ${
                  cap === bucket
                    ? 'border-accent text-accent bg-accent/[0.08]'
                    : 'border-border-subtle text-fg-secondary hover:border-border-default'
                } ${!enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {bucket}
              </button>
            ))}
          </div>
        </div>

        {update.isError && (
          <p className="text-sm text-negative">
            Couldn't save preferences: {(update.error as Error).message ?? 'unknown error'}
          </p>
        )}
      </div>
    </Card>
  );
}
